import React, { useEffect, useState } from 'react';
import api from '../api/client';
import { coaApi } from '../api/coa';
import ContactPicker from '../components/ContactPicker';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';

interface Props { paymentId: number; onBack: () => void; }

export default function SupplierPaymentDetail({ paymentId, onBack }: Props) {
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const [id, setId] = useState(paymentId);
  const [pay, setPay] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [form, setForm] = useState<any>({});
  const [splits, setSplits] = useState<any[]>([]);
  const fmt = (n: any) => Number(n||0).toLocaleString('en-US', { minimumFractionDigits:2, maximumFractionDigits:2 });

  const load = () => {
    setLoading(true);
    api.get(`/supplier-payments/${id}`).then(r => { setPay(r.data.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(load, [id]); // eslint-disable-line

  const openEdit = () => {
    coaApi.list().then(r => setAccounts((r.data.data || []).filter((a: any) =>
      a.type_name === 'Asset' && !['1100','1200','1300'].includes(String(a.account_code)) && !/^1[5-9]/.test(String(a.account_code)))));
    api.get('/supplier-invoices').then(r => setInvoices(r.data.data || [])).catch(() => {});
    setForm({
      contact_id: String(pay.contact_id), contact_name: pay.supplier_name,
      purchase_invoice_id: pay.purchase_invoice_id ? String(pay.purchase_invoice_id) : '',
      payment_date: (pay.payment_date || pay.paid_at || '').substring(0, 10),
      payment_ref: pay.payment_ref || '', notes: pay.notes || '',
    });
    setSplits((pay.splits || []).map((s: any) => ({ account_id: String(s.account_id), amount: String(s.amount) })));
    setEditing(true);
  };

  const total = splits.reduce((t, s) => t + Number(s.amount || 0), 0);

  const save = async () => {
    if (!form.contact_id) { showToast('Supplier is required', 'error'); return; }
    if (!form.payment_date) { showToast('Date is required', 'error'); return; }
    if (!splits.length || splits.some(s => !s.account_id || !(Number(s.amount) > 0))) { showToast('Every line needs an account and an amount', 'error'); return; }
    setSaving(true);
    try {
      const res = await api.put(`/supplier-payments/${id}`, {
        contact_id: Number(form.contact_id), branch_id: Number(pay.branch_id),
        purchase_invoice_id: form.purchase_invoice_id ? Number(form.purchase_invoice_id) : null,
        payment_date: form.payment_date, payment_ref: form.payment_ref || null, notes: form.notes || null,
        splits: splits.map(s => ({ account_id: Number(s.account_id), amount: Number(s.amount) })),
      });
      showToast(res.data.message);
      setEditing(false);
      setId(res.data.data.id);
    } catch (e: any) { showToast(e.response?.data?.message || 'Save failed', 'error'); }
    finally { setSaving(false); }
  };

  const doVoid = async () => {
    const reason = window.prompt('Why is this payment being voided?');
    if (reason === null) return;
    try {
      const res = await api.post(`/supplier-payments/${id}/void`, { reason: reason || null });
      showToast(res.data.message);
      onBack();
    } catch (e: any) { showToast(e.response?.data?.message || 'Void failed', 'error'); }
  };

  if (loading) return <div className="empty-state"><p>Loading...</p></div>;
  if (!pay) return <div className="empty-state"><p>Not found.</p></div>;

  if (editing) {
    const supplierInvoices = invoices.filter((i: any) => String(i.contact_id) === String(form.contact_id));
    return (
      <div>
        <div className="page-header">
          <h1 className="page-title">Correct Supplier Payment #{pay.id}</h1>
          <div style={{ display:'flex', gap:8 }}>
            <button className="btn btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save correction'}</button>
          </div>
        </div>
        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-body">
            <div className="form-grid-2">
              <div className="form-group"><label className="form-label">Supplier *</label>
                <ContactPicker value={form.contact_id} initialLabel={form.contact_name} onSelect={(c: any) => setForm((f: any) => ({ ...f, contact_id: String(c.id), contact_name: c.name, purchase_invoice_id: '' }))} />
              </div>
              <div className="form-group"><label className="form-label">Invoice</label>
                <select className="form-control" value={form.purchase_invoice_id} onChange={e => setForm((f: any) => ({ ...f, purchase_invoice_id: e.target.value }))}>
                  <option value="">General payment</option>
                  {supplierInvoices.map((i: any) => <option key={i.id} value={i.id}>{i.invoice_no} — {fmt(i.invoice_amount)}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Date *</label>
                <input type="date" className="form-control" value={form.payment_date} onChange={e => setForm((f: any) => ({ ...f, payment_date: e.target.value }))} />
              </div>
              <div className="form-group"><label className="form-label">Reference</label>
                <input className="form-control" value={form.payment_ref} onChange={e => setForm((f: any) => ({ ...f, payment_ref: e.target.value }))} />
              </div>
            </div>
            <div className="form-group"><label className="form-label">Notes</label>
              <input className="form-control" value={form.notes} onChange={e => setForm((f: any) => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-header"><span>Paid from</span></div>
          <div className="card-body">
            {splits.map((s, i) => (
              <div key={i} style={{ display:'flex', gap:6, marginBottom:6 }}>
                <select className="form-control" style={{ flex:1 }} value={s.account_id} onChange={e => setSplits(x => x.map((y, j) => j === i ? { ...y, account_id: e.target.value } : y))}>
                  <option value="">Select account</option>
                  {accounts.map((a: any) => <option key={a.id} value={a.id}>{a.account_code} - {a.account_name}</option>)}
                </select>
                <input className="form-control mono" style={{ width:130, textAlign:'right' }} placeholder="0.00" value={s.amount} onChange={e => setSplits(x => x.map((y, j) => j === i ? { ...y, amount: e.target.value } : y))} />
                {splits.length > 1 && <button className="btn btn-secondary btn-sm" onClick={() => setSplits(x => x.filter((_, j) => j !== i))}>x</button>}
              </div>
            ))}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:8 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setSplits(x => [...x, { account_id:'', amount:'' }])}>+ Add account</button>
              <div style={{ fontSize:13 }}>Total <strong style={{ fontFamily:'monospace' }}>{fmt(total)}</strong> <span style={{ color:'var(--tx3)', fontSize:11 }}>(was {fmt(pay.total)})</span></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header" id="no-print">
        <button className="btn btn-secondary" onClick={onBack}>Back</button>
        <div style={{ display:'flex', gap:8 }}>
          {hasPermission('supplier_payments.void') && <button className="btn btn-secondary" onClick={doVoid}>Void</button>}
          {hasPermission('supplier_payments.edit') && <button className="btn btn-secondary" onClick={openEdit}>Correct</button>}
          <button className="btn btn-primary" onClick={() => window.print()}>Print</button>
        </div>
      </div>
      <div className="card" id="print-area">
        <div style={{ padding:'20px 24px', borderBottom:'1px solid var(--bdr)', display:'flex', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontSize:20, fontWeight:700 }}>Supplier Payment</div>
            <div style={{ fontSize:12, color:'var(--tx3)', marginTop:4 }}>#{pay.id} &mdash; {pay.branch_name}</div>
          </div>
          <div style={{ textAlign:'right', fontSize:12, color:'var(--tx3)' }}>
            <div>Date: {(pay.payment_date || pay.paid_at) ? new Date(pay.payment_date || pay.paid_at).toLocaleDateString() : '-'}</div>
            <div>Ref: {pay.payment_ref || '-'}</div>
            {pay.invoice_no && <div>Invoice: {pay.invoice_no}</div>}
          </div>
        </div>
        <div style={{ padding:'16px 24px', borderBottom:'1px solid var(--bdr)' }}>
          <div style={{ fontSize:12, color:'var(--tx3)', marginBottom:4 }}>Supplier</div>
          <div style={{ fontWeight:600 }}>{pay.supplier_name}</div>
        </div>
        <div style={{ padding:'16px 24px', borderBottom:'1px solid var(--bdr)' }}>
          <div style={{ fontSize:12, color:'var(--tx3)', marginBottom:6 }}>Paid from</div>
          {(pay.splits || []).map((s: any) => (
            <div key={s.id} style={{ display:'flex', justifyContent:'space-between', fontSize:13, padding:'3px 0' }}>
              <span>{s.account_code} - {s.account_name}</span><span style={{ fontFamily:'monospace' }}>{fmt(s.amount)}</span>
            </div>
          ))}
          {pay.notes && <div style={{ fontSize:12, color:'var(--tx3)', marginTop:8 }}>Notes: {pay.notes}</div>}
        </div>
        <div style={{ padding:'24px', display:'flex', justifyContent:'flex-end' }}>
          <div style={{ textAlign:'right' }}>
            <div style={{ fontSize:13, color:'var(--tx3)', marginBottom:4 }}>Amount Paid</div>
            <div style={{ fontSize:28, fontWeight:700, color:'var(--red)' }}>{fmt(pay.total ?? pay.amount)}</div>
          </div>
        </div>
      </div>
      <style>{`@media print { #no-print { display:none !important; } }`}</style>
    </div>
  );
}
